// The outbox: what the patient sent that the server has not stored yet
// (blhWebServer ADR 0018 §5). The same model as the clinic's
// (blhClinicApp src/features/messaging/realtime/outbox.js); a change to one is
// made to both.
//
// "Sent" means stored. A message the person sends goes here first and shows in
// its conversation as "Sending…"; it leaves when the server answers with the
// stored message (or the room's event for it arrives first, matched by its
// `clientMessageId`). Nothing typed is lost: until the server has it, the text
// lives here, and a send that fails shows "Not sent" with Retry and Remove.
//
// - One id per message, minted once (`clientMessageId`, the request's
//   Idempotency-Key): a retry, automatic or the person's, is the same request,
//   and the server answers it with the message already stored, never a second.
// - In order: a conversation's sends go one after another, so the server
//   stamps them in the order they were sent. Another conversation's do not wait.
// - A failure the same request may survive (no answer, a server error, too
//   many at once) is retried twice, after 1 s and 3 s, then waits for the
//   person. A refusal (not a member, archived, a file already sent) is shown
//   with the server's sentence and is not retried.
// - What a message needs before it can be sent is its `prepare`, run once,
//   in its turn: a first message in a new direct conversation creates the
//   conversation (its real id replaces the draft's); a file is uploaded (the
//   stored file becomes its attachment). A prepare that fails is the send's
//   failure, and Retry runs it again.

export const OUTBOX_STATUS = Object.freeze({
  SENDING: "sending",
  FAILED: "failed",
});

const RETRY_DELAYS_MS = Object.freeze([1000, 3000]);
const RETRYABLE_STATUSES = new Set([408, 425, 429]);
// A 409 the same request survives: the server is still running it (idempotency), or it was busy.
const RETRYABLE_CODES = new Set([
  "REQUEST_IN_PROGRESS",
  "TOO_MANY_MESSAGES",
  "SERVER_ERROR",
]);
const FALLBACK_SENTENCE = "Not sent. Check your connection, then retry.";

/** Whether the same request may succeed later: no answer, a server error, or the server saying "not now". */
export function isRetryable(error) {
  const status = error?.status;
  if (!Number.isInteger(status) || status === 0) return true;
  if (RETRYABLE_CODES.has(error?.code)) return true;
  return status >= 500 || RETRYABLE_STATUSES.has(status);
}

/**
 * @param {object} deps
 * @param {(entry) => Promise<{conversationId: string, message: object}>} deps.send   the server's send
 * @param {(conversationId: string, message: object) => void} deps.onStored           the stored message, for the thread
 * @param {(ms: number) => Promise<void>} [deps.wait]                                  a test's clock
 */
export function createOutbox({
  send,
  onStored,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}) {
  let entries = [];
  const listeners = new Set();
  const chains = new Map();

  function publish(next) {
    entries = next;
    for (const listener of listeners) listener();
  }

  const find = (clientMessageId) =>
    entries.find((entry) => entry.clientMessageId === clientMessageId) || null;

  function update(clientMessageId, change) {
    publish(
      entries.map((entry) =>
        entry.clientMessageId === clientMessageId
          ? {...entry, ...change}
          : entry
      )
    );
  }

  function remove(clientMessageId) {
    const entry = find(clientMessageId);
    if (entry) publish(entries.filter((each) => each !== entry));
    return entry;
  }

  async function attempt(clientMessageId) {
    for (let tries = 0; ; tries += 1) {
      let entry = find(clientMessageId);
      if (!entry || entry.status !== OUTBOX_STATUS.SENDING) return;
      try {
        if (entry.prepare) {
          const prepared = await entry.prepare();
          update(clientMessageId, {...preparedOf(prepared), prepare: null});
          entry = find(clientMessageId);
          if (!entry) return;
        }
        const stored = await send(entry);
        if (find(clientMessageId)) remove(clientMessageId);
        onStored(stored.conversationId || entry.conversationId, stored.message);
        return;
      } catch (error) {
        if (!find(clientMessageId)) return;
        const retryable = isRetryable(error);
        if (retryable && tries < RETRY_DELAYS_MS.length) {
          await wait(RETRY_DELAYS_MS[tries]);
          continue;
        }
        update(clientMessageId, {
          status: OUTBOX_STATUS.FAILED,
          error: {
            sentence: (!retryable && error?.message) || FALLBACK_SENTENCE,
            retryable,
          },
        });
        return;
      }
    }
  }

  /** One after another per conversation: the next send starts when the one before has an answer. */
  function schedule(key, clientMessageId) {
    const next = (chains.get(key) || Promise.resolve()).then(() =>
      attempt(clientMessageId)
    );
    chains.set(key, next);
    next.finally(() => {
      if (chains.get(key) === next) chains.delete(key);
    });
  }

  return {
    /** The outbox's entries, in the order they were sent (a new array on each change). */
    getSnapshot: () => entries,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /**
     * A message to send: `{conversationId, clientMessageId, messageType, message, attachment?}`,
     * and `prepare()` when it needs something first: it resolves to the conversation's real
     * id, or to `{conversationId?, message?, attachment?}`, merged into the message.
     */
    enqueue({prepare = null, ...payload}) {
      if (find(payload.clientMessageId)) return;
      publish([
        ...entries,
        {
          ...payload,
          prepare,
          draftId: prepare ? payload.conversationId : null,
          status: OUTBOX_STATUS.SENDING,
          error: null,
        },
      ]);
      schedule(payload.conversationId, payload.clientMessageId);
    },
    /** The person's retry of a send that failed: the same message, the same id. */
    retry(clientMessageId) {
      const entry = find(clientMessageId);
      if (!entry || entry.status !== OUTBOX_STATUS.FAILED) return;
      update(clientMessageId, {status: OUTBOX_STATUS.SENDING, error: null});
      schedule(entry.conversationId, clientMessageId);
    },
    /** The person gives up on a failed send; returns it, so its text can go back to the composer. */
    discard(clientMessageId) {
      const entry = find(clientMessageId);
      if (!entry || entry.status !== OUTBOX_STATUS.FAILED) return null;
      return remove(clientMessageId);
    },
    /** The room's event for a message this screen sent: it is stored, whatever the request still says. */
    acknowledge(clientMessageId) {
      if (typeof clientMessageId === "string") remove(clientMessageId);
    },
  };
}

/** What a prepare gives a message: a conversation's id, or the fields it now knows. */
function preparedOf(prepared) {
  if (typeof prepared === "string") return {conversationId: prepared};
  const known = {};
  for (const field of ["conversationId", "message", "attachment"]) {
    if (prepared?.[field] !== undefined) known[field] = prepared[field];
  }
  return known;
}

/** A conversation's entries: its own, and those still waiting for a draft of it to be created. */
export function entriesOf(entries, conversationId) {
  return entries.filter(
    (entry) =>
      entry.conversationId === conversationId ||
      entry.draftId === conversationId
  );
}
